import { config } from "../config";

// Microsoft OAuth 2.0 configuration for Authorization Code flow with PKCE
export const microsoftConfig = {
  clientId: config.microsoftClientId,
  tenantId: config.microsoftTenantId,
  redirectUri: config.microsoftRedirectUri,
  scopes: ["openid", "profile", "email", "User.Read"],
  responseType: "code",
  responseMode: "query",
};

// Generate secure random code_verifier and code_challenge
export const generateCodeVerifier = (): string => {
  const array = new Uint8Array(32);
  crypto.getRandomValues(array);
  return Array.from(array, (byte) => byte.toString(16).padStart(2, "0")).join(
    ""
  );
};

// Generate code_challenge based on code_verifier (using SHA-256)
export const generateCodeChallenge = async (
  codeVerifier: string
): Promise<string> => {
  const encoder = new TextEncoder();
  const data = encoder.encode(codeVerifier);
  const hash = await window.crypto.subtle.digest("SHA-256", data);
  const uint8Array = new Uint8Array(hash);
  const base64String = btoa(
    Array.from(uint8Array)
      .map((byte) => String.fromCharCode(byte))
      .join("")
  );
  const base64Url = base64String
    .replace(/\+/g, "-")
    .replace(/\//g, "_")
    .replace(/=+$/, "");
  return base64Url;
};

// Generate Microsoft OAuth authorization URL with PKCE
export const getMicrosoftAuthUrl = async (state: string): Promise<string> => {
  const codeVerifier = generateCodeVerifier();
  const codeChallenge = await generateCodeChallenge(codeVerifier);

  // Save the code_verifier for later use during token exchange
  localStorage.setItem("code_verifier", codeVerifier);

  const params = new URLSearchParams({
    client_id: microsoftConfig.clientId,
    response_type: microsoftConfig.responseType,
    redirect_uri: microsoftConfig.redirectUri,
    response_mode: microsoftConfig.responseMode,
    scope: microsoftConfig.scopes.join(" "),
    state: state,
    prompt: "select_account",
    code_challenge: codeChallenge,
    code_challenge_method: "S256", // Specifies the SHA-256 method for code challenge
  });

  const authority =
    microsoftConfig.tenantId === "common"
      ? "https://login.microsoftonline.com/common"
      : `https://login.microsoftonline.com/${microsoftConfig.tenantId}`;

  return `${authority}/oauth2/v2.0/authorize?${params.toString()}`;
};

// Parse callback URL parameters and retrieve code_verifier
export const parseMicrosoftCallback = (): {
  code?: string;
  state?: string;
  error?: string;
} => {
  const urlParams = new URLSearchParams(window.location.search);

  return {
    code: urlParams.get("code") || undefined,
    state: urlParams.get("state") || undefined,
    error: urlParams.get("error") || undefined,
  };
};

// After receiving the authorization code, use it with code_verifier to get the token
export const getAccessToken = async (
  authorizationCode: string
): Promise<any> => {
  const codeVerifier = localStorage.getItem("code_verifier");

  if (!codeVerifier) {
    throw new Error("Code verifier not found");
  }

  const params = new URLSearchParams({
    client_id: microsoftConfig.clientId,
    grant_type: "authorization_code",
    code: authorizationCode,
    redirect_uri: microsoftConfig.redirectUri,
    code_verifier: codeVerifier!,
  });

  const response = await fetch(
    `https://login.microsoftonline.com/${microsoftConfig.tenantId}/oauth2/v2.0/token`,
    {
      method: "POST",
      headers: {
        "Content-Type": "application/x-www-form-urlencoded",
      },
      body: params.toString(),
    }
  );

  return response.json();
};

// Generate secure random state for CSRF protection
export const generateState = (): string => {
  const array = new Uint8Array(32);
  crypto.getRandomValues(array);
  return Array.from(array, (byte) => byte.toString(16).padStart(2, "0")).join(
    ""
  );
};
